/**
 * Phase D — Billing / subscription RTK Query.
 *
 * Wraps the existing `:app/subscriptions/*` + `:app/plans` + `:app/products`
 * endpoints on main-service (already in production for ghostnews / weelia /
 * growth — same URL shape, `:app=promptlab`).
 *
 * The subscription create flow uses **Stripe hosted Checkout**:
 *  1. POST /subscriptions/init-subscription → returns `{ data: { url } }`
 *  2. Frontend `window.location.assign(url)` → user pays on Stripe
 *  3. Stripe redirects to /billing/success or /billing/cancel
 *
 * Credit top-ups use the same shape via `purchase-session`.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryRegistryWithReauth } from './baseQuery';
import { mainServiceAppURL } from './defines';

// ─── Types ────────────────────────────────────────────────────────────

/** A row from `:app/plans`. */
export interface Plan {
  id: string;
  name: string;
  description?: string;
  /** Display price in the company's billing currency. */
  price?: number;
  /** Tier slug, e.g. 'tier-2'. Available via the `tags` array on Roles. */
  priority: number;
  /** `tokens` (subscription with monthly credits) | `flat` | etc. */
  type: string;
  /** Monthly credits granted by this plan. */
  nTokens: number;
  trial: boolean;
  roleID: string;
  /** Plan-level metadata. Stripe priceID lives here under
   *  `metadata.subscription.priceID` or `metadata.subscription.priceId`. */
  metadata?: {
    public?: boolean;
    signupBalance?: number;
    interactionsIncluded?: number;
    surface?: string;
    annualBillingSupported?: boolean;
    shareViewersOnly?: boolean;
    contact?: string;
    subscription?: {
      productID?: string;
      priceID?: string;
      priceId?: string;
      monthly?: { priceID?: string; price?: number };
      annual?: { priceID?: string; price?: number };
    };
  };
  isDeleted?: boolean;
}

/** Active subscription for the current company, returned by `/current`. */
export interface CurrentSubscription {
  id: string;
  planID: string;
  plan: Plan;
  startDate: string;
  renewalDate: string;
  status: 'active' | 'paymentFailed' | 'canceled' | 'trial';
  tokens?: { currentPlan: number; purchased: number };
  autoTopUp?: boolean;
  autoTopUpValue?: number;
  providerMetadata?: { grandfathered?: boolean; priceID?: string; billingCycle?: string };
}

/** Credit-package product (from `/products/type/credits`). */
export interface CreditPackage {
  id: string;
  name: string;
  description?: string;
  type: 'credits';
  amount: number; // credits granted
  price?: number; // display price
  metadata?: {
    subscription?: { priceID?: string };
  };
}

/** Entitlements payload from registry's GET /v1/auth/me/entitlements. */
export interface EntitlementsResponse {
  tier: 'tier-0' | 'tier-1' | 'tier-2' | 'tier-3' | 'tier-4';
  planName: string;
  canExecute: boolean;
  canViewAnalytics: boolean;
  canBatchTest: boolean;
  canShareProject: boolean;
  canUseLabAssistant: boolean;
  canInviteEditors: boolean;
  creditsRemaining: { plan: number; purchased: number };
  interactionsRemaining: { included: number; packages: number } | null;
  costMultiplier: number;
  enforceCredits: boolean;
  grandfathered: boolean;
}

/** Body for POST /subscriptions/init-subscription. */
export interface InitSubscriptionBody {
  email: string;
  planID: string;
  metadata: {
    planInfo: {
      planID: string;
      priceID?: string;
      billingCycle?: 'month' | 'year';
    };
    companyInfo?: {
      companyID?: string;
      name?: string;
    };
    billingInfo?: {
      name?: string;
      email?: string;
      taxNumber?: string;
      address?: {
        line1?: string;
        line2?: string;
        city?: string;
        state?: string;
        postalCode?: string;
        country?: string;
      };
    };
    itemID?: string;
    itemType?: string;
  };
}

// ─── API ──────────────────────────────────────────────────────────────

export const billingApi = createApi({
  reducerPath: 'billingApi',
  baseQuery: baseQueryRegistryWithReauth, // re-uses cookie auth + auto-refreshes 401s
  tagTypes: ['Subscriptions', 'Plans', 'Products', 'Entitlements', 'Invoices'],
  endpoints: (builder) => ({
    // ── Plans (catalog) ────────────────────────────────────────────
    fetchPlans: builder.query<{ data: Plan[] }, void>({
      query: () => ({
        url: `${mainServiceAppURL}/plans`,
        method: 'GET',
      }),
      providesTags: ['Plans'],
    }),

    // ── Credit packages ────────────────────────────────────────────
    fetchCreditPackages: builder.query<{ data: CreditPackage[] }, void>({
      query: () => ({
        url: `${mainServiceAppURL}/products/type/credits`,
        method: 'GET',
      }),
      providesTags: ['Products'],
    }),

    // ── Current subscription ───────────────────────────────────────
    fetchCurrentSubscription: builder.query<{ data: CurrentSubscription[] }, void>({
      query: () => ({
        url: `${mainServiceAppURL}/subscriptions/current`,
        method: 'GET',
      }),
      providesTags: ['Subscriptions'],
    }),

    // ── Init subscription → returns Stripe Checkout URL ────────────
    initSubscription: builder.mutation<
      { data: { sessionId: string; url: string } },
      InitSubscriptionBody
    >({
      query: (body) => ({
        url: `${mainServiceAppURL}/subscriptions/init-subscription`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Subscriptions'],
    }),

    // ── Buy credit-package on existing subscription ────────────────
    buyTokens: builder.mutation<
      { data: any },
      {
        email: string;
        productID: string;
        quantity?: number;
        metadata?: Record<string, any>;
      }
    >({
      query: (body) => ({
        url: `${mainServiceAppURL}/subscriptions/buy-tokens`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Subscriptions'],
    }),

    // ── Stripe Checkout for top-up — works without a Stripe customerId
    //    on the user's subscription (grandfathered subs, fresh signups).
    //    Backend resolves a Stripe customer by the authenticated user's
    //    email (find or create) and starts a one-time checkout session.
    createBuyCreditsSession: builder.mutation<
      { data: { sessionId: string; url: string; customerId?: string } },
      {
        priceId: string;
        quantity?: number;
        returnUrl?: string;
      }
    >({
      query: (body) => ({
        url: `${mainServiceAppURL}/subscriptions/buy-credits-session`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Subscriptions'],
    }),

    // ── Stripe Checkout for top-up on existing sub ─────────────────
    createPurchaseSession: builder.mutation<
      { data: { sessionId: string; url: string } },
      {
        customerId: string;
        priceId: string;
        quantity: number;
        transactionId?: string;
        subscriptionID?: string;
        returnUrl: string;
      }
    >({
      query: (body) => ({
        url: `${mainServiceAppURL}/subscriptions/purchase-session`,
        method: 'POST',
        body,
      }),
    }),

    // ── Stripe billing portal (cancel / change card / invoices) ────
    createBillingPortalSession: builder.mutation<
      { data: { url: string } },
      { customerId: string }
    >({
      query: (body) => ({
        url: `${mainServiceAppURL}/subscriptions/update-billing`,
        method: 'POST',
        body,
      }),
    }),

    // ── Invoices ────────────────────────────────────────────────────
    fetchInvoices: builder.query<{ data: any[] }, { id: string }>({
      query: ({ id }) => ({
        url: `${mainServiceAppURL}/subscriptions/${id}/invoices`,
        method: 'GET',
      }),
      providesTags: ['Invoices'],
    }),

    // ── Entitlements (registry) ────────────────────────────────────
    fetchEntitlements: builder.query<EntitlementsResponse, void>({
      query: () => ({
        // Goes via the registry gateway. baseQueryRegistry already
        // points at the registry's URL prefix; no /v1 needed here.
        url: '/auth/me/entitlements',
        method: 'GET',
      }),
      providesTags: ['Entitlements'],
    }),

    // ── Cancel / unsubscribe ───────────────────────────────────────
    unsubscribe: builder.mutation<{ data: any }, { id: string }>({
      query: ({ id }) => ({
        url: `${mainServiceAppURL}/subscriptions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Subscriptions'],
    }),

    // ── Update auto-topup ──────────────────────────────────────────
    updateSubscription: builder.mutation<
      { data: any },
      { id: string; autoTopUp?: boolean; autoTopUpValue?: number }
    >({
      query: ({ id, ...body }) => ({
        url: `${mainServiceAppURL}/subscriptions/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Subscriptions'],
    }),
  }),
});

export const {
  useFetchPlansQuery,
  useFetchCreditPackagesQuery,
  useFetchCurrentSubscriptionQuery,
  useInitSubscriptionMutation,
  useBuyTokensMutation,
  useCreatePurchaseSessionMutation,
  useCreateBuyCreditsSessionMutation,
  useCreateBillingPortalSessionMutation,
  useFetchInvoicesQuery,
  useFetchEntitlementsQuery,
  useUnsubscribeMutation,
  useUpdateSubscriptionMutation,
} = billingApi;
